import humanizeDuration from 'humanize-duration';
import { addSeconds, isSameDay, format, parseISO } from 'date-fns';
/* convert JIRA time (e.g 1d 1h 20m) to seconds
 * (2.5h) to seconds
 */
export function jts(jiraTimeString) {
  const nums = jiraTimeString.match(/\d+(\.\d+)?/g) ?? [];
  const units = jiraTimeString.match(/[a-zA-Z]+/g) ?? [];

  let resultSeconds = 0;
  nums.forEach((num, index) => {
    const value = Number(num);
    const postfix = units[index];
    switch (postfix) {
      case 'd':
        resultSeconds += value * 86400;
        break;
      case 'h':
        resultSeconds += value * 3600;
        break;
      case 's':
        resultSeconds += value;
        break;
      case 'm':
      default:
        resultSeconds += value * 60;
        break;
    }
  });
  return resultSeconds;
}

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
  units: ['h', 'm', 's', 'ms'],
  delimiter: ' ',
  spacer: '',
  largest: 2,
});

// convert seconds to JIRA time (e.g 10h 30m) using given format
export function stj(seconds, defaultStr = '') {
  return seconds ? shortEnglishHumanizer(seconds * 1000) : defaultStr;
}

export function getTimeString(seconds) {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const moreThanOneHour = hours >= 1;

  const secondsRelatedMinutes = minutes >= 1 ? seconds - minutes * 60 : seconds;
  const minutesRelatedHours = hours >= 1 ? minutes - hours * 60 : minutes;
  const minutesFormat =
    minutesRelatedHours >= 10
      ? minutesRelatedHours.toString()
      : `0${minutesRelatedHours}`;
  const secondsFormat =
    secondsRelatedMinutes >= 10
      ? secondsRelatedMinutes.toString()
      : `0${secondsRelatedMinutes}`;
  const hoursFormat = hours >= 10 ? hours.toString() : `0${hours}`;

  if (moreThanOneHour) {
    return `${hoursFormat}:${minutesFormat}:${secondsFormat}`;
  }
  return `${minutesFormat}:${secondsFormat}`;
}

export function getWorklogTime(time) {
  if (time === 0) {
    return '0m';
  }
  const mind = time % 3600;
  const minutes = Math.floor(mind / 60);
  const hours = Math.floor(time / 3600);

  const secd = mind % 60;
  const seconds = Math.ceil(secd);

  const m = minutes > 0 ? `${minutes}m ` : '';
  const s = seconds > 0 ? `${seconds}s` : '';
  const h = hours > 0 ? `${hours}h ` : '';
  return `${h}${m}${s}`.trim();
}

export const getWorklogGroupTimePoints = ({ worklogs }) => {
  const firstWorklog = worklogs[0];
  const lastWorklog = worklogs[worklogs.length - 1];
  const from = parseISO(firstWorklog.startedAt);
  const to = addSeconds(parseISO(lastWorklog.startedAt), lastWorklog.seconds);

  if (!isSameDay(from, to)) {
    const fromDate = format(from, 'MMM d HH:mm');
    const toDate = format(to, 'MMM d HH:mm');

    return `${fromDate} - ${toDate}`;
  }

  const fromTime = format(from, 'HH:mm');
  const toTime = format(to, 'HH:mm');
  const isSameTime = fromTime === toTime;

  if (isSameTime) {
    return `${fromTime}`;
  }

  return `${fromTime} - ${toTime}`;
};

export const getWorklogTimePoints = ({ from, seconds }) => {
  const to = addSeconds(from, seconds);

  if (!isSameDay(from, to)) {
    const fromDate = format(from, 'MMM d HH:mm');
    const toDate = format(to, 'MMM d HH:mm');

    return `${fromDate} - ${toDate}`;
  }

  const fromTime = format(from, 'HH:mm');
  const toTime = format(to, 'HH:mm');
  const isSameTime = fromTime === toTime;

  if (isSameTime) {
    return `${fromTime}`;
  }

  return `${fromTime} - ${toTime}`;
};

export function formatWorklogCreationDate(date) {
  return format(date, "LLL d 'at' T");
}

export function calculateTimeProgress({ trackedTime, estimate }) {
  const leftCalc = estimate
    ? parseInt((trackedTime / estimate) * 100, 10)
    : 100;
  const progressStats = {
    isOverEstimate: leftCalc > 100,
    leftPercentage:
      leftCalc > 100 ? parseInt((estimate / trackedTime) * 100, 10) : leftCalc,
    rightPercentage: leftCalc > 100 ? 100 : 100 - leftCalc,
    leftMark:
      trackedTime > 0 ? `${stj(trackedTime)} logged` : 'nothing logged yet',
    rightMark: '',
  };
  if (leftCalc > 100) {
    progressStats.rightMark = `${stj(trackedTime - estimate)} over estimate`;
  }
  if (leftCalc < 100) {
    progressStats.rightMark =
      trackedTime === 0
        ? `${stj(estimate)} remaining`
        : `${stj(estimate - trackedTime)} remaining`;
  }
  return progressStats;
}

export const getUsersStatsMap = (
  usersIds,
  usersEntities,
  secondsMap,
  estimates
) => {
  const map = {};
  usersIds.forEach((userId) => {
    const user = usersEntities[userId];
    const estimate = estimates.find((e) => e.accountId === user.id);
    const loggedSeconds = secondsMap[user.id];

    map[user.id] = {
      id: user.id,
      loggedSeconds: loggedSeconds || 0,
      loggedString: stj(loggedSeconds),
      estimatedSeconds: estimate?.seconds || 0,
      estimatedString: stj(estimate?.seconds || 0),
      user,
    };
  });
  return map;
};

export const getTotalEstimate = (estimates) =>
  estimates.reduce((total, estimate) => total + estimate.seconds, 0);
